import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { uniq, shuffle } from 'underscore';
import Slider from 'react-slick';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import configData from '../../signals/configData';
import translation from '../../signals/translations';
import useData from '../../hooks/useData';
import IconClose from '../../icons/IconClose';
import IconHeart from '../../icons/IconHeart';
import IconHeartFilled from '../../icons/IconHeartFilled';
import IconMarkerSearchPlain from '../../icons/IconMarkerSearchPlain';
import IconInstagram from '../../icons/IconInstagram';
import IconFacebook from '../../icons/IconFacebook';
import IconLINE from '../../icons/IconLINE';
import IconLink from '../../icons/IconLink';
import IconTag from '../../icons/IconTag';
import IconBrand from '../../icons/IconBrand';
import IconGlobe from '../../icons/IconGlobe';
import IconMail from '../../icons/IconMail';
import IconLocation from '../../icons/IconLocation';
import useRecord from '../../hooks/useRecord';
import textareaToHTML from '../../tools/textareaToHTML';
import splitMultiValue from '../../tools/splitMultiValue';
import { productCategoryPaths } from '../../tools/mergeDuplicateProducts';
import { boothMatesOf, exhibitorKeyOf, findExhibitorByKey } from '../../tools/boothExhibitors';
import useRecommend from '../../hooks/useRecommend';

const Prev = (props) => {
	return <div className='nav prev' onClick={props.onClick} />;
};

const Next = (props) => {
	return <div className='nav next' onClick={props.onClick} />;
};

const ExhibitorInfo = () => {
	const { exhibitorInfo, floorInfo, currentLanguage } = jsonData.value;
	const { ExhibitorInfo, selectedProduct, selectedExhibitor } = appState.value;
	const { recommendFromAPI, showRightContent } = configData.value;
	const { toggleFavourite } = useData();
	const [product, setProduct] = useState(null);
	// 使用 useRef 以避免 callback ref 在 mount/unmount 間切換為 null 造成競態
	const sliderRef = useRef(null);
	const autoNavigateTriggeredRef = useRef(false);
	const { makeRecord } = useRecord();
	const { fetchRecommend, relatedExhibitorsFromAPI } = useRecommend();

	// 直接使用 sliderRef 掛載在 Slider 上

	useEffect(() => {
		if (selectedProduct) {
			setProduct(selectedProduct);
		}
	}, [selectedProduct]);

	const exhibitor = useMemo(() => {
		return findExhibitorByKey(exhibitorInfo, selectedExhibitor);
	}, [exhibitorInfo, selectedExhibitor]);

	// 跟這家掛在同一個地圖圖形上的其他廠商。單獨一家的圖形是空陣列，右欄就不會多出那一塊。
	const boothMates = useMemo(() => {
		if (!exhibitor) return [];
		return boothMatesOf(floorInfo, exhibitorInfo, exhibitor);
	}, [exhibitor, exhibitorInfo, floorInfo]);

	const logoUrlOf = useCallback((someone) => {
		const logo = someone?.LOGO || '';
		if (!logo) return null;
		return logo.includes('http') ? logo : `./exhibitor-logos/${logo}`;
	}, []);

	// translations.json 是客戶那邊維護的檔案，新鍵不一定補上；缺的時候退回內建文字，不要空白。
	const sameBoothTitle = useMemo(() => {
		const 退路 = { 'zh-Hant': '同攤位', 'zh-Hans': '同摊位', en: 'Same Booth' };
		return translation('exhibitorPopup.sameBooth') || 退路[currentLanguage?.code] || 退路['zh-Hant'];
	}, [currentLanguage?.code]);

	// useEffect(() => {
	// 	if (exhibitor) {
	// 		exhibitor.products = [...exhibitor.products, ...exhibitor.products, ...exhibitor.products];
	// 	}
	// }, [exhibitor]);

	const closePopup = useCallback(() => {
		appState.value = {
			...appState.value,
			ExhibitorInfo: false,
		};

		setTimeout(() => {
			appState.value = {
				...appState.value,
				selectedExhibitor: null,
				selectedProduct: null,
				selectedBooth: null,
			};

			setProduct(null);
		}, 500);
	}, [setProduct]);

	const toggleFavouriteExhibitor = useCallback(() => {
		toggleFavourite(exhibitor);
	}, [exhibitor, toggleFavourite]);

	const categories = useMemo(() => {
		const allCategories = [];

		exhibitor?.products.forEach((product) => {
			// 一個產品可能掛好幾條分類路徑（顯示前已經併成一筆），每一條都要各自取最細的那一層。
			// 只看第一條的話，彈窗上的分類標籤會少掉幾個，而且畫面上看不出來少了什麼。
			productCategoryPaths(product).forEach((path) => {
				if (path['產品小分類']) {
					allCategories.push(path['產品小分類']);
					return;
				}

				if (path['產品中分類']) {
					allCategories.push(path['產品中分類']);
					return;
				}

				if (path['產品大分類']) {
					allCategories.push(path['產品大分類']);
					return;
				}
			});
		});

		const uniqueCategories = uniq(allCategories);

		return uniqueCategories;
	}, [exhibitor]);

	const categoryLevel = useMemo(() => {
		const firstPath = productCategoryPaths(exhibitor?.products[0])[0];
		if (!firstPath) return null;
		if (firstPath['產品小分類']) return '產品小分類';
		if (firstPath['產品中分類']) return '產品中分類';
		if (firstPath['產品大分類']) return '產品大分類';
		return null;
	}, [exhibitor]);

	const relatedExhibitors = useMemo(() => {
		const relatedExhibitors = [];
		const otherExhibitors = exhibitorInfo.filter((exhibitor) => exhibitorKeyOf(exhibitor) !== selectedExhibitor);

		otherExhibitors.forEach((exhibitor) => {
			const newExhibitor = { ...exhibitor };

			// 原本這裡是三段一模一樣的程式，只差在讀哪一個欄位；categoryLevel 本身就是那個欄位名稱，
			// 所以直接拿它當 key。行為沒有變，只是不會再有三段各自改壞的機會。
			// 產品掛多條分類路徑時，任何一條落在 categories 裡就算相關。
			if (categoryLevel) {
				newExhibitor.products = exhibitor.products.filter((product) => {
					return productCategoryPaths(product).some((path) => categories.includes(path[categoryLevel]));
				});
			}

			if (newExhibitor.products.length > 0) {
				relatedExhibitors.push(newExhibitor);
			}
		});

		return shuffle(relatedExhibitors);
	}, [exhibitorInfo, selectedExhibitor, categoryLevel, categories]);

	useEffect(() => {
		if (exhibitor && exhibitor['攤位號碼']) {
			fetchRecommend(exhibitor['攤位號碼']);
		}
	}, [exhibitor, fetchRecommend]);

	const recordExhibitorClicked = useCallback(
		(booth) => {
			makeRecord({
				Action: 'T',
				Booth: booth,
			});
		},
		[makeRecord]
	);

	const recordProductClicked = useCallback(
		(product, booth) => {
			makeRecord({
				Action: 'T',
				Booth: booth || exhibitor?.['攤位號碼'],
				Product: product['產品名稱'],
			});
		},
		[makeRecord, exhibitor]
	);

	const showProduct = useCallback(
		(product) => {
			recordProductClicked(product);
			setProduct(product);
		},
		[recordProductClicked]
	);

	const chooseExhibitor = (exhibitor) => {
		appState.value = {
			...appState.value,
			activeFloor: Number(exhibitor.floor),
			selectedBooth: exhibitor['攤位號碼'],
			selectedExhibitor: exhibitorKeyOf(exhibitor),
		};

		setProduct(null);
	};

	const chooseProduct = useCallback(
		(relatedExhibitor, index) => {
			appState.value = {
				...appState.value,
				selectedExhibitor: exhibitorKeyOf(relatedExhibitor),
				activeFloor: Number(relatedExhibitor.floor),
				selectedBooth: relatedExhibitor['攤位號碼'],
			};

			const product = relatedExhibitor.products[index];

			setProduct(product);
			recordProductClicked(product, relatedExhibitor['攤位號碼']);
		},
		[recordProductClicked]
	);

	useEffect(() => {
		// 僅在有 slick 實例時才做跳轉，避免 null 讀取
		const instance = sliderRef.current;
		if (exhibitor && product && instance && typeof instance.slickGoTo === 'function') {
			const exhibitorProductIndex = exhibitor.products.findIndex((p) => p['產品名稱'] === product['產品名稱']);
			if (exhibitorProductIndex >= 0) {
				instance.slickGoTo(exhibitorProductIndex);
			}
		}
	}, [exhibitor, product]);

	const close = useCallback(
		(event) => {
			if (event.target === event.currentTarget) {
				appState.value = {
					...appState.value,
					ExhibitorInfo: false,
				};

				setTimeout(() => {
					appState.value = {
						...appState.value,
						selectedExhibitor: null,
						selectedProduct: null,
						selectedBooth: null,
					};

					setProduct(null);
				}, 500);
			}
		},
		[setProduct]
	);

	// 是否使用 slick：產品數 > 3 才啟用；否則顯示靜態卡片
	const productCount = exhibitor?.products.length || 0;
	const useSlider = productCount > 3;

	const slidesToShow = useMemo(() => {
		if (window.innerWidth <= 768) {
			// 行動版維持原本：>1 顯示 2，否則 1
			return productCount > 1 ? 2 : 1;
		}
		// 桌機版使用 slick 時固定顯示 3；不使用 slick 時不影響（靜態）
		return 3;
	}, [productCount]);

	const scrollerRef = useRef(null);

	const navigateToBooth = useCallback(() => {
		makeRecord({
			Action: 'N',
			Booth: exhibitor?.['攤位號碼'],
		});

		const isMobile = window.innerWidth < 800;

		appState.value = {
			...appState.value,
			ExhibitorInfo: false,
			...(isMobile
				? {
						ExhibitorList: false,
						SearchPanel: false,
						MobileMenu: false,
				  }
				: {}),
			selectedExhibitor,
			selectedBooth: exhibitor?.['攤位號碼'],
			navigation: true,
		};
	}, [exhibitor, makeRecord, selectedExhibitor]);

	useEffect(() => {
		autoNavigateTriggeredRef.current = false;
	}, [selectedExhibitor]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const navi = params.get('navi');
		if (!navi || !selectedExhibitor || !exhibitor || autoNavigateTriggeredRef.current) return;
		// ?navi= 可以給攤位號碼、序號或名稱，跟 index.js 認廠商的方式一樣：任何一個字串欄位對得上就算。
		const 對得上 = Object.values(exhibitor).some((value) => typeof value === 'string' && value.toUpperCase() === navi.toUpperCase());
		if (!對得上) return;

		autoNavigateTriggeredRef.current = true;
		navigateToBooth();
	}, [exhibitor, navigateToBooth, selectedExhibitor]);

	const city = useMemo(() => {
		if (exhibitor) {
			return exhibitor['所在城市'];
		}
		return null;
	}, [exhibitor]);

	// E-mail 在介紹文字裡的那一行（連標籤一起），抓到才有值。
	// 抽出來做成可點的連結之後，要把原本那一行從介紹裡拿掉，不然同一個信箱會出現兩次：
	// 上面一個可以點的、下面一個點不下去的 —— 而點不下去的那個正是客戶要我們修掉的東西。
	const 介紹裡的Email行 = useMemo(() => {
		if (!exhibitor) return null;
		const 介紹 = exhibitor['展商基本介紹'];
		if (!介紹) return null;
		return String(介紹).match(
			/^[ \t]*(?:e-?mail|電子郵件|信箱)[ \t]*[:：][ \t]*([^\s<>()（），,；;、]+@[^\s<>()（），,；;、]+)[ \t]*$/im
		);
	}, [exhibitor]);

	const email = useMemo(() => {
		if (!exhibitor) return null;

		// 先找獨立欄位。欄位名稱由客戶那邊的匯入檔決定，來信講的是「EMAIL 欄位」，
		// 但實際填進 CSV 的寫法還沒定案，所以幾種常見的都收。
		// 只認一種的話，欄位名對不上時是安靜地不顯示 —— 看起來會像這個功能沒做。
		const keys = ['EMAIL', 'Email', 'email', 'E-mail', 'E-MAIL', '電子郵件', '信箱'];
		const key = keys.find((k) => exhibitor[k]);
		if (key) return String(exhibitor[key]).trim();

		// 沒有獨立欄位就退而求其次，從「展商基本介紹」的文字裡把 E-mail 抓出來。
		//
		// 🔴 這一段不是備胎，是目前唯一會生效的那一條路。
		// 客戶現在沒有 E-mail 欄位，他們是把「E-mail：xxx@yyy」直接打在介紹的第一行
		// （2026-09-14 那份 AMB 資料 62 家裡有 59 家是這樣）。
		// 只讀獨立欄位的話，這個功能交出去畫面上完全不會有變化。
		//
		// ⚠ 只認前面有標籤（E-mail／Email／電子郵件／信箱）的那一種寫法，
		// 不要在整段介紹裡亂抓 @ —— 介紹文字裡出現別人的信箱、或社群帳號的 @xxx，
		// 都會被抓成廠商的聯絡信箱，而畫面上看起來一樣正常。
		if (介紹裡的Email行) return 介紹裡的Email行[1].replace(/[.,;、。]+$/, '');

		return null;
	}, [exhibitor, 介紹裡的Email行]);

	// 介紹文字。E-mail 是從裡面抽出來的話，就把那一行連同它後面的空行一起拿掉。
	const 介紹 = useMemo(() => {
		const 原文 = exhibitor?.['展商基本介紹'] || '';
		if (!介紹裡的Email行) return 原文;
		return 原文.replace(介紹裡的Email行[0], '').replace(/^\s*\n+/, '');
	}, [exhibitor, 介紹裡的Email行]);

	const logoUrl = useMemo(() => {
		if (exhibitor) {
			if (exhibitor.LOGO.includes('http')) {
				return exhibitor.LOGO;
			}
			return `./exhibitor-logos/${exhibitor.LOGO}`;
		}
		return null;
	}, [exhibitor]);

	// 品牌名稱可以填多個，同一格用分號隔開 —— 跟廠商的「區域」同一個慣例。
	// 只填一個的時候回傳只有一項的陣列，畫面跟以前一樣。
	const brands = useMemo(() => splitMultiValue(exhibitor?.['品牌名稱']), [exhibitor]);

	const [relatedExhibitorsToDisplay, setRelatedExhibitorsToDisplay] = useState(3);

	const recommendedExhibitors = useMemo(() => {
		let recommendedExhibitors = [];

		if (recommendFromAPI) recommendedExhibitors = relatedExhibitorsFromAPI;
		if (!recommendFromAPI) recommendedExhibitors = relatedExhibitors;

		if (recommendedExhibitors.length > relatedExhibitorsToDisplay) {
			return recommendedExhibitors.slice(0, relatedExhibitorsToDisplay);
		}

		return recommendedExhibitors;
	}, [recommendFromAPI, relatedExhibitorsFromAPI, relatedExhibitors, relatedExhibitorsToDisplay]);

	const showMoreButton = useMemo(() => {
		if (relatedExhibitors.length > relatedExhibitorsToDisplay) {
			return true;
		}
		return false;
	}, [relatedExhibitors, relatedExhibitorsToDisplay]);

	useEffect(() => {
		if (selectedExhibitor) {
			setRelatedExhibitorsToDisplay(3);
		}
	}, [selectedExhibitor]);

	const rightContent = useMemo(() => {
		const params = new URLSearchParams(window.location.search);
		const sug = params.get('sug');
		const configSUG = showRightContent;

		// 有同攤位的其他家時右欄一定要開，不然那一塊沒地方放。
		if (boothMates.length > 0) return true;
		if (!configSUG) return false;
		if (configSUG && sug === 'false') return false;
		return true;
	}, [boothMates.length, showRightContent]);

	const productImageUrl = useMemo(() => {
		if (product) {
			if (product['產品圖'].includes('http')) {
				return product['產品圖'];
			}
			return `./product-photos/${product['產品圖']}`;
		}
		return null;
	}, [product]);

	useEffect(() => {
		if (exhibitor && !product) {
			if (exhibitor.products.length) {
				setProduct(exhibitor.products[0]);
			}
		}
	}, [exhibitor, product, setProduct]);

	if (!exhibitor) return null;

	return (
		<div id='ExhibitorInfo' className={ExhibitorInfo ? 'active' : ''} onClick={close}>
			<div className={`center-content ${rightContent ? '' : 'narrow'}`}>
				<IconClose onClick={closePopup} />

				<div className='logo-wrap'>
					<img src={logoUrl} alt='LOGO' />
				</div>

				<div className='title'>{exhibitor['廠商名稱']}</div>

				<div className='scroller' ref={scrollerRef}>
					<div className='flex-wrap-fixed'>
						<div className='left-content'>
							<div className='flex-between'>
								<div className='controls'>
									<div className='flex-control' onClick={toggleFavouriteExhibitor}>
										<div className='icon'>{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}</div>
										<div className='text'>
											{translation('exhibitorPopup.booth')}：{exhibitor['攤位號碼']}
										</div>
									</div>

									<div className='flex-control' onClick={navigateToBooth}>
										<div className='icon marker'>
											<IconMarkerSearchPlain />
										</div>
										<div className='text'>{translation('exhibitorPopup.navigationPath')}</div>
									</div>
								</div>

								<div className='links'>
									{exhibitor['社群連結-IG'] ? (
										<a href={exhibitor['社群連結-IG']} target='_blank' rel='noreferrer noopener'>
											<IconInstagram />
										</a>
									) : null}

									{exhibitor['社群連結-FB'] ? (
										<a href={exhibitor['社群連結-FB']} target='_blank' rel='noreferrer noopener'>
											<IconFacebook />
										</a>
									) : null}

									{exhibitor['社群連結-LINE'] ? (
										<a href={exhibitor['社群連結-LINE']} target='_blank' rel='noreferrer noopener'>
											<IconLINE />
										</a>
									) : null}

									{/* 「其他」沒有自己的品牌圖示，用通用的連結圖示。
									    IG、FB、LINE 各自認得出來，這一個只能靠位置與 hover。 */}
									{exhibitor['社群連結-其他'] ? (
										<a href={exhibitor['社群連結-其他']} target='_blank' rel='noreferrer noopener'>
											<IconLink />
										</a>
									) : null}
								</div>
							</div>

							{/* 客戶 2026-09-17：紅框那幾行純文字光看認不出是什麼欄位（網址跟 E-mail 都是一行藍字、
							    地址是一行灰字），所以每一個欄位前面加一個圖示。
							    版型統一用 .field（圖示一欄、內容一欄），圖示欄寬度固定不跟著文字縮。 */}
							{categories.length ? (
								<div className='tags'>
									<div className='field'>
										<IconTag />
										<div className='tag-list'>
											{categories.map((category, index) => (
												<div className='tag' key={`uc${index}`}>
													#{category}
												</div>
											))}
										</div>
									</div>
								</div>
							) : null}

							{/* 品牌名稱跟公司網址放在同一個區塊（客戶 2026-09-14 指定的位置，原本在公司名稱底下）。
							    品牌名稱可以有多個，同一格用分號隔開，跟「區域」是同一個慣例。
							    兩個都沒填的話整個區塊不出現，不要留一條空的分隔線。 */}
							{brands.length || exhibitor['官網連結'] ? (
								<div className='website'>
									{brands.length ? (
										<div className='field'>
											<IconBrand />
											<div className='brands'>
												{brands.map((brand, index) => (
													<span className='brand' key={`brand${index}`}>
														{brand}
													</span>
												))}
											</div>
										</div>
									) : null}

									{exhibitor['官網連結'] ? (
										<div className='field'>
											<IconGlobe />
											<a href={exhibitor['官網連結']} target='_blank' rel='noreferrer noopener'>
												{exhibitor['官網連結']}
											</a>
										</div>
									) : null}
								</div>
							) : null}

							{email ? (
								<div className='email'>
									<div className='field'>
										<IconMail />
										<a href={`mailto:${email}`}>{email}</a>
									</div>
								</div>
							) : null}

							{/* 地址兩格都空的時候整列不要出現，不然會留一條只有圖示的空行 ——
							    沒有圖示的時候那一列是完全看不見的，加了圖示才會露餡。 */}
							{city || exhibitor['國別'] ? (
								<div className='location'>
									<div className='field'>
										<IconLocation />
										<span>
											{city}
											{city && exhibitor['國別'] && `, `}
											{exhibitor['國別']}
										</span>
									</div>
								</div>
							) : null}

							<div className='description' dangerouslySetInnerHTML={{ __html: textareaToHTML(介紹) }} />

							{exhibitor['影片連結'] ? (
								<div className='video'>
									<iframe
										src={exhibitor['影片連結'].replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')}
										title='YouTube video'
										frameBorder='0'
										allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
										allowFullScreen
									/>
								</div>
							) : null}

							<div className={`products ${useSlider ? '' : 'static'}`}>
								{useSlider ? (
									<Slider
										infinite={true}
										slidesToShow={slidesToShow}
										dots={false}
										arrows={true}
										swipeToSlide={true}
										prevArrow={<Prev />}
										nextArrow={<Next />}
										ref={sliderRef}
										responsive={[
											{
												breakpoint: 768,
												settings: {
													slidesToShow: slidesToShow,
												},
											},
											{
												breakpoint: 480,
												settings: {
													slidesToShow: 1,
												},
											},
										]}>
										{exhibitor.products.map((product, index) => {
											const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
											return (
												<div className='product-wrap' key={`exhibitor-product-${index}`} onClick={() => showProduct(product)}>
													<div className='product'>
														<div className='image-wrap' style={{ backgroundImage: `url(${imageUrl})` }}></div>
														<div className='product-name'>{product['產品名稱']}</div>
													</div>
												</div>
											);
										})}
									</Slider>
								) : (
									// 靜態顯示（不使用 slick）
									exhibitor.products.map((product, index) => {
										const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
										return (
											<div className='product-wrap' key={`exhibitor-product-static-${index}`} onClick={() => showProduct(product)}>
												<div className='product'>
													<div className='image-wrap' style={{ backgroundImage: `url(${imageUrl})` }}></div>
													<div className='product-name'>{product['產品名稱']}</div>
												</div>
											</div>
										);
									})
								)}
							</div>

							{product ? (
								<div className='product-detail'>
									<div className='product-image' style={{ backgroundImage: `url(${productImageUrl})` }}></div>
									<div className='product-name'>{product['產品名稱']}</div>
									<div className='product-details' dangerouslySetInnerHTML={{ __html: textareaToHTML(product['產品說明']) }}></div>
								</div>
							) : null}

							<div className='mobile-products'>
								{exhibitor.products.map((prod, index) => {
									const imageUrl = prod['產品圖'].includes('http') ? prod['產品圖'] : `./product-photos/${prod['產品圖']}`;

									return (
										<div className='product-detail-mobile' key={`prodetail${index}`}>
											<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
											<div className='product-name'>{prod['產品名稱']}</div>
											<div className='product-details' dangerouslySetInnerHTML={{ __html: textareaToHTML(prod['產品說明']) }}></div>
										</div>
									);
								})}
							</div>
						</div>

						{rightContent ? (
							<div className='right-content'>
								{boothMates.length > 0 ? (
									<div className='same-booth'>
										<div className='content-title'>{sameBoothTitle}</div>
										{boothMates.map((mate) => {
											const logo = logoUrlOf(mate);
											return (
												<div
													className='mate'
													key={`mate-${exhibitorKeyOf(mate)}`}
													onClick={() => {
														chooseExhibitor(mate);
														recordExhibitorClicked(mate['攤位號碼']);
													}}>
													<div className='mate-logo'>{logo ? <img src={logo} alt='' /> : null}</div>
													<div className='mate-name'>{mate['廠商名稱']}</div>
												</div>
											);
										})}
									</div>
								) : null}

								<div className='content-title'>{translation('exhibitorPopup.recommended')}</div>

								<div className='related-exhibitors'>
									{recommendedExhibitors.map((exhibitor, index) => {
										return (
											<div className='exhibitor' key={`related-exhibitor-${index}`}>
												<div
													className='exhibitor-name'
													onClick={() => {
														chooseExhibitor(exhibitor);
														recordExhibitorClicked(exhibitor['攤位號碼']);
													}}>
													{exhibitor['廠商名稱']}
												</div>
												<div className='related-products'>
													{exhibitor.products.map((product, i) => {
														if (i > 2) return null;
														const imageUrl = product['產品圖'].includes('http') ? product['產品圖'] : `./product-photos/${product['產品圖']}`;
														return (
															<div
																className='product-wrap'
																key={`related-product-${index}${i}`}
																onClick={() => {
																	chooseProduct(exhibitor, i, product);
																	if (scrollerRef.current && typeof scrollerRef.current.scrollTo === 'function') {
																		scrollerRef.current.scrollTo(0, 0);
																	}
																}}>
																<div className='product-image' style={{ backgroundImage: `url(${imageUrl})` }}></div>
																<div className='product-name'>{product['產品名稱']}</div>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>

								{showMoreButton ? (
									<div className='flex-button'>
										<div
											className='show-more'
											onClick={() => {
												setRelatedExhibitorsToDisplay(relatedExhibitorsToDisplay + 3);
											}}>
											{translation('exhibitorPopup.showMore')}
										</div>
									</div>
								) : null}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ExhibitorInfo;
